'use client'

import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Link } from '@src/quince-components/src'
import { BUTTON_SIZE, BUTTON_TYPES } from '@src/quince-components/src/atoms/button/button.constants'
import { EDDMessageContainer } from '@src/quince-components/src/atoms/eddMessageContainer'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import { ProductDisplayButton } from '@src/quince-components/src/atoms/productCTA/product-display-button'
import { ProductDisplayText } from '@src/quince-components/src/atoms/productCTA/product-display-text'
import { InStockAndReadyToShip, RenderErrorMessage } from '@src/quince-components/src/atoms/productCTA/productCTA'
import { CTA_TYPE } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import { VariantPurchaseInfo } from '@src/quince-components/src/atoms/productForm/ProductForm'
import { PurchaseType } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import WaitlistSmsModal from '@src/quince-components/src/atoms/waitlist-sms-modal'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { AppQRModal, useShopInAppHandler } from '@src/quince-site-common/src/hooks/appQrModal'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import styles from './miniPDPProductCTA.module.scss'
import { useMiniPDPProductCTA } from './useMiniPDPProductCTA'

const MiniPDPProductCTA = ({
  product,
  selectedOptions,
  type,
  showInStockAndReadyToShip,
  selectedVariantPurchaseInfo,
  errorMessage,
  setEmail,
  isAppQRModalOpen,
  handleCloseAppQRModal,
  handleClick,
  isAddToCartInProgress,
  isPending,
  disableAddToCart,
  isGoldBarError,
  isCurrentProductOptionLeavingSoon,
}: {
  product: Product
  selectedOptions: ProductVariantOption[]
  type: CTA_TYPE
  selectedVariantPurchaseInfo: VariantPurchaseInfo | undefined
  showInStockAndReadyToShip?: boolean
  errorMessage: string
  setEmail: Dispatch<SetStateAction<string>>
  isAppQRModalOpen: boolean
  handleCloseAppQRModal: () => void
  handleClick: () => void
  isAddToCartInProgress?: boolean
  isPending?: boolean
  disableAddToCart?: boolean
  isGoldBarError?: boolean
  isCurrentProductOptionLeavingSoon?: boolean
}) => {
  const router = useRouter()
  const deviceInfo = useDeviceInfo()
  const { t } = useTranslation('common')
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const productLink = getProductLink(product, selectedOptions)

  const handleFullDetailsClick = () => {
    const variants = productHelper.getVariantsForOptions(product, selectedOptions)
    const selectedVariant = variants[0]
    trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_FULL_DETAILS_VIEWED, {
      context: {
        productProperties: getProductPropertiesForTracking(product, selectedVariant?.svid?.toString(), {}),
      },
    })
    router.push(productLink)
  }

  const renderCTA = () => {
    const buttonSize = isDesktop ? BUTTON_SIZE.LARGE : BUTTON_SIZE.SMALL

    switch (type) {
      case CTA_TYPE.PREORDER:
        return (
          <ProductDisplayButton
            onClick={handleClick}
            buttonSize={buttonSize}
            showLoader={isPending || isAddToCartInProgress}
            className={styles.productButton}
            showNewPdpDesignV2={true}
          >
            {t('preorder')}
          </ProductDisplayButton>
        )

      case CTA_TYPE.WAITLIST:
        return (
          <ProductDisplayButton
            onClick={handleClick}
            buttonSize={buttonSize}
            buttonType={isCurrentProductOptionLeavingSoon ? BUTTON_TYPES.PRIMARY : BUTTON_TYPES.SECONDARY}
            showLoader={isAddToCartInProgress}
            className={styles.productButton}
            showNewPdpDesignV2={true}
          >
            {isCurrentProductOptionLeavingSoon
              ? t(ProductCTAConstants.SHOP_SIMILAR)
              : selectedVariantPurchaseInfo?.hasJoinedWaitList
                ? t(ProductCTAConstants.WAITLISTED)
                : t(ProductCTAConstants.JOIN_WAITLIST)}
          </ProductDisplayButton>
        )

      case CTA_TYPE.OUT_OF_STOCK:
        return (
          <ProductDisplayButton
            onClick={() => undefined}
            disabled={true}
            buttonSize={buttonSize}
            className={styles.productButton}
            showNewPdpDesignV2={true}
          >
            {t('out_of_stock')}
          </ProductDisplayButton>
        )

      case CTA_TYPE.SHOP_IN_THE_APP:
        return (
          <ProductDisplayButton
            onClick={handleClick}
            buttonSize={buttonSize}
            className={styles.productButton}
            buttonType={BUTTON_TYPES.SECONDARY}
            showNewPdpDesignV2={true}
          >
            {t('shop_in_the_app')}
          </ProductDisplayButton>
        )

      default:
        return (
          <ProductDisplayButton
            onClick={handleClick}
            showLoader={isPending || isAddToCartInProgress}
            className={styles.productButton}
            buttonSize={buttonSize}
            showNewPdpDesignV2={true}
            disabled={disableAddToCart || isGoldBarError}
          >
            {t(atcLabel)}
          </ProductDisplayButton>
        )
    }
  }

  return (
    <div className={styles.productCTAWrapper}>
      {showInStockAndReadyToShip ? (
        <div className={styles.stockMessageWrapper}>
          <InStockAndReadyToShip inStockMsgClass={styles.inStock} showNewPdpDesignV2={true} />
        </div>
      ) : null}
      {type === CTA_TYPE.WAITLIST && !isCurrentProductOptionLeavingSoon && (
        <>
          <ProductDisplayText
            date={selectedVariantPurchaseInfo?.backInStockDate}
            showNewPdpDesignV2={true}
            className={styles.waitListedText}
          />
          {!selectedVariantPurchaseInfo?.hasJoinedWaitList && !AuthUtil.isLoggedIn() && (
            <input
              onChange={e => {
                setEmail?.(e.target.value)
              }}
              className={styles.waitlistInput}
              placeholder={t('enter_your_email_address')}
              type="email"
            />
          )}
        </>
      )}
      {type === CTA_TYPE.WAITLIST && isCurrentProductOptionLeavingSoon && (
        <RenderErrorMessage errorMessage={t(ProductCTAConstants.ITEM_NO_LONGER_AVAILABLE)} showNewPdpDesignV2={true} />
      )}
      {errorMessage && <RenderErrorMessage errorMessage={errorMessage} showNewPdpDesignV2={true} />}
      {type === CTA_TYPE.OUT_OF_STOCK && !errorMessage && (
        <RenderErrorMessage errorMessage={t('out_of_stock')} showNewPdpDesignV2={true} />
      )}
      <div>
        {isDesktop ? (
          renderCTA()
        ) : (
          <div className={styles.mobileCTAWrapper}>
            <ProductDisplayButton
              buttonType={BUTTON_TYPES.OUTLINED}
              onClick={handleFullDetailsClick}
              showLoader={false}
              className={styles.productButton}
              buttonSize={BUTTON_SIZE.SMALL}
            >
              {t('full_details')}
            </ProductDisplayButton>
            {renderCTA()}
          </div>
        )}
        {isDesktop && (
          <div className={styles.details}>
            <span className={styles.details__text}>
              <Link href={productLink} onClick={handleFullDetailsClick} aria-label={t('view_full_product_details')}>
                {t('view_full_details')}
              </Link>
            </span>
          </div>
        )}
      </div>
      <AppQRModal
        isOpen={isAppQRModalOpen}
        product={product}
        selectedOptions={selectedOptions}
        onClose={handleCloseAppQRModal}
      />
    </div>
  )
}

const MiniPDPProductCTAWrapper = (props: {
  product: Product
  selectedOptions: ProductVariantOption[]
  selectedVariantPurchaseInfo: VariantPurchaseInfo | undefined
  productCTAType: CTA_TYPE
  areAllVariantOOS: boolean
  inputOptionError: string
  productFormErrorMessage: string
  onAddToCart: (isPreorder?: boolean, selectedPlanId?: number) => Promise<void>
  onJoinTheWaitList: (email?: string) => void
  showPurchaseTypeSelector?: boolean
  purchaseOption?: PurchaseType
  selectedPlanId?: number
  isAddToCartInProgress?: boolean
  selectedVariantAvailableInventory: number
  showVariantAvailableInventory: boolean
  disableAddToCart?: boolean
  isCurrentProductOptionLeavingSoon?: boolean
  onClickedShopSimilar?: () => void
}) => {
  const { selectedVariantPurchaseInfo, productCTAType } = props
  const { t } = useTranslation('common')

  const { isAppQRModalOpen, handleShopInApp, handleCloseAppQRModal } = useShopInAppHandler()

  const {
    setEmail,
    showInStockAndReadyToShip,
    errorMessage,
    handleClick,
    showEDDMsg,
    isGoldBarError,
    isPending,
    isBisWaitlistSmsEnabled,
    showSmsModal,
    setShowSmsModal,
    onSubmitPhone,
  } = useMiniPDPProductCTA({
    product: props.product,
    selectedOptions: props.selectedOptions,
    productCTAType,
    selectedVariantPurchaseInfo,
    areAllVariantOOS: props.areAllVariantOOS,
    onAddToCart: props.onAddToCart,
    onJoinWaitList: props.onJoinTheWaitList,
    handleShopInApp: handleShopInApp,
    showPurchaseTypeSelector: props.showPurchaseTypeSelector,
    purchaseOption: props.purchaseOption,
    selectedPlanId: props.selectedPlanId,
    isCurrentProductOptionLeavingSoon: props.isCurrentProductOptionLeavingSoon,
    onClickedShopSimilar: props.onClickedShopSimilar,
  })

  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const eddDetail = selectedVariantPurchaseInfo?.eddDetail

  return (
    <>
      {props.showVariantAvailableInventory && (
        <div className={styles.inventoryMessage}>
          {t('hurry_only', { quantity: props.selectedVariantAvailableInventory })}
        </div>
      )}
      {props.productFormErrorMessage && (
        <RenderErrorMessage errorMessage={props.productFormErrorMessage} showNewPdpDesignV2={true} />
      )}
      {showEDDMsg && !isGoldBarError ? (
        <EDDMessageContainer
          eddDetail={eddDetail}
          product={props.product}
          selectedInternalVariantId={selectedVariantPurchaseInfo?.selectedInternalVariantId}
          containerClassName={styles.eddContainer}
          showIcon={!isDesktop}
          showNewPdpDesignV2={true}
        />
      ) : null}
      <MiniPDPProductCTA
        product={props.product}
        selectedOptions={props.selectedOptions}
        type={productCTAType}
        selectedVariantPurchaseInfo={selectedVariantPurchaseInfo}
        showInStockAndReadyToShip={showInStockAndReadyToShip}
        errorMessage={errorMessage}
        setEmail={setEmail}
        handleClick={handleClick}
        isAppQRModalOpen={isAppQRModalOpen}
        handleCloseAppQRModal={handleCloseAppQRModal}
        isAddToCartInProgress={props.isAddToCartInProgress}
        isPending={isPending}
        disableAddToCart={props.disableAddToCart}
        isGoldBarError={isGoldBarError}
        isCurrentProductOptionLeavingSoon={props.isCurrentProductOptionLeavingSoon}
      />
      {isBisWaitlistSmsEnabled ? (
        <WaitlistSmsModal
          open={showSmsModal}
          onClose={() => {
            setShowSmsModal(false)
          }}
          onSubmitPhone={onSubmitPhone}
        />
      ) : null}
    </>
  )
}
export default MiniPDPProductCTAWrapper
